import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { API_URL } from "@/config";
import axios from "axios";
import { FileText, Lock, ChevronDown, ChevronUp } from "lucide-react";

const http = axios.create({ baseURL: API_URL, withCredentials: true });

interface CreditApp {
    credit_app_fullblown_id: number;
    applicant_fname: string | null;
    applicant_lname: string | null;
    applicant_email_address: string | null;
    applicant_cell_phone: string | null;
    credit_app_locked: number | null;
    credit_app_source: string | null;
    application_created_date: string | null;
    app_deal_id?: number | null;
}

interface Deal {
    deal_id: number;
    deal_status: string | null;
    deal_type: string | null;
    deal_locked: boolean;
    vYear: number | null;
    vMake: string | null;
    vModel: string | null;
    priceVehicle: string | null;
    monthlypymts: string | null;
    deal_created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
    PENDING: "bg-yellow-100 text-yellow-700",
    ACCEPTED: "bg-green-100 text-green-700",
    DECLINED: "bg-red-100 text-red-700",
    FUNDED: "bg-blue-100 text-blue-700",
};

export default function FinanceDashboard() {
    const { user } = useAuth();
    const [apps, setApps] = useState<CreditApp[]>([]);
    const [deals, setDeals] = useState<Deal[]>([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState<number | null>(null);
    const [filter, setFilter] = useState<"ALL" | "LOCKED" | "OPEN">("ALL");

    useEffect(() => {
        Promise.all([
            http.get<CreditApp[]>("/finance/credit-apps").then(r => r.data).catch(() => []),
            http.get<Deal[]>("/finance/deals").then(r => r.data).catch(() => []),
        ]).then(([a, d]) => {
            setApps(a);
            setDeals(d);
        }).finally(() => setLoading(false));
    }, []);

    const filteredApps = apps.filter(a => {
        if (filter === "LOCKED") return a.credit_app_locked === 1;
        if (filter === "OPEN") return !a.credit_app_locked;
        return true;
    });

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-light uppercase tracking-wide">Finance Manager Dashboard</h1>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                    { label: "Credit Apps", value: apps.length, icon: FileText, color: "text-blue-600 bg-blue-50" },
                    { label: "Locked Deals", value: deals.filter(d => d.deal_locked).length, icon: Lock, color: "text-green-600 bg-green-50" },
                    { label: "Pending Review", value: apps.filter(a => !a.credit_app_locked).length, icon: FileText, color: "text-yellow-600 bg-yellow-50" },
                ].map(s => (
                    <div key={s.label} className="bg-white rounded-2xl shadow p-5 flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${s.color}`}>
                            <s.icon className="h-5 w-5" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{loading ? "—" : s.value}</p>
                            <p className="text-xs text-gray-500 uppercase">{s.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Credit Apps Table */}
            <div className="bg-white rounded-2xl shadow p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="font-semibold text-lg">Credit Applications</h2>
                    <div className="flex gap-2">
                        {(["ALL", "LOCKED", "OPEN"] as const).map(f => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                className={`px-3 py-1 text-xs rounded-full border transition ${filter === f ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 text-gray-500 hover:border-indigo-400"}`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <p className="text-sm text-gray-400">Loading...</p>
                ) : filteredApps.length === 0 ? (
                    <p className="text-sm text-gray-400">No credit applications found.</p>
                ) : (
                    <div className="space-y-2">
                        {filteredApps.map(app => (
                            <div key={app.credit_app_fullblown_id} className="border rounded-xl overflow-hidden">
                                <button
                                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
                                    onClick={() => setExpanded(expanded === app.credit_app_fullblown_id ? null : app.credit_app_fullblown_id)}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="font-medium">{app.applicant_fname} {app.applicant_lname}</span>
                                        {app.credit_app_locked === 1 && (
                                            <Lock className="h-3 w-3 text-green-600" />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="text-xs text-gray-400">
                                            {app.application_created_date ? new Date(app.application_created_date).toLocaleDateString() : "—"}
                                        </span>
                                        {expanded === app.credit_app_fullblown_id ? (
                                            <ChevronUp className="h-4 w-4 text-gray-400" />
                                        ) : (
                                            <ChevronDown className="h-4 w-4 text-gray-400" />
                                        )}
                                    </div>
                                </button>

                                {expanded === app.credit_app_fullblown_id && (
                                    <div className="px-4 pb-4 pt-2 bg-gray-50 border-t grid grid-cols-2 gap-3 text-sm">
                                        <div><span className="text-gray-400">Email:</span> {app.applicant_email_address ?? "—"}</div>
                                        <div><span className="text-gray-400">Phone:</span> {app.applicant_cell_phone ?? "—"}</div>
                                        <div><span className="text-gray-400">Source:</span> {app.credit_app_source ?? "—"}</div>
                                        <div><span className="text-gray-400">Linked Deal:</span> {app.app_deal_id ?? "None"}</div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Locked Deals */}
            <div className="bg-white rounded-2xl shadow p-6">
                <h2 className="font-semibold text-lg mb-4">Locked Deals</h2>
                {loading ? (
                    <p className="text-sm text-gray-400">Loading...</p>
                ) : deals.filter(d => d.deal_locked).length === 0 ? (
                    <p className="text-sm text-gray-400">No locked deals yet.</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-xs uppercase text-gray-400 border-b">
                            <tr>
                                <th className="text-left py-2">Vehicle</th>
                                <th className="text-left py-2">Type</th>
                                <th className="text-left py-2">Price</th>
                                <th className="text-left py-2">Monthly</th>
                                <th className="text-left py-2">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {deals.filter(d => d.deal_locked).map(d => (
                                <tr key={d.deal_id}>
                                    <td className="py-2">{[d.vYear, d.vMake, d.vModel].filter(Boolean).join(" ") || `Deal #${d.deal_id}`}</td>
                                    <td className="py-2">{d.deal_type ?? "—"}</td>
                                    <td className="py-2">{d.priceVehicle ? `$${Number(d.priceVehicle).toLocaleString()}` : "—"}</td>
                                    <td className="py-2">{d.monthlypymts ? `$${Number(d.monthlypymts).toLocaleString()}` : "—"}</td>
                                    <td className="py-2">
                                        <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[d.deal_status ?? "PENDING"] ?? "bg-gray-100 text-gray-600"}`}>
                                            {d.deal_status ?? "PENDING"}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
